import { createSignal } from "solid-js";

/**
 * Organización (empresa / supermercado) ACTIVA por usuario — estado compartido
 * a nivel de MÓDULO para que TODAS las instancias de `useActiveOrg` (una por
 * ruta del WMS + el switcher del header) reaccionen al mismo cambio, y
 * persistido en localStorage para sobrevivir recargas.
 *
 * La clave es un mapa `userId → scopeId` (no un valor plano) porque la app es
 * multi-cuenta: cada cuenta recuerda SU organización activa y un switch de
 * cuenta no hereda el puntero de otra (mismo espíritu que el multi-restaurante
 * de comandero y el workspace-switcher de time-tracker).
 *
 * `scopeId` puede ser:
 *   - el id de una organización real (empresa donde el usuario es miembro), o
 *   - el propio `userId` → workspace PERSONAL (inventario `organization_id =
 *     user_id`, F1; también el modo visitante con `VISITOR_ID`).
 *
 * El puntero es solo una PREFERENCIA: `useActiveOrg` lo valida contra las orgs
 * realmente sincronizadas en local y cae a "Personal" (el userId) si ya no
 * existe (org borrada / dato viejo / aún sin sincronizar) — nunca bloquea
 * (auto-sanación).
 */
const STORAGE_KEY = "stockpilot.activeOrgByUser";

type ActiveMap = Readonly<Record<string, string>>;

function readPersisted(): ActiveMap {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const entries = Object.entries(parsed).filter(
        (entry): entry is [string, string] => typeof entry[1] === "string",
      );
      return Object.fromEntries(entries);
    }
  } catch {
    // localStorage puede fallar (modo privado) o el JSON estar corrupto; el
    // estado en memoria basta y el fallback es "Personal".
  }
  return {};
}

const [activeMap, setActiveMap] = createSignal<ActiveMap>(readPersisted());

/**
 * Epoch de CAMBIO EXPLÍCITO de organización. Se incrementa SOLO cuando el
 * usuario cambia de org a propósito (`setActiveScopeIdFor`), nunca durante el
 * asentamiento del scope en el boot (userId visitante→real, sync tardío…).
 *
 * El guard de aislamiento (`useResetOnOrgSwitch`) observa este epoch para
 * descartar punteros de entidad abierta (`?picking=`, `?po=`, `?count=`…) al
 * cambiar de org, SIN el riesgo de borrar un deep-link legítimo durante el
 * arranque (que sí ocurriría observando el scope directamente).
 */
const [switchEpoch, setSwitchEpoch] = createSignal(0);

/** Puntero de org activa del usuario (puede apuntar a una org ya inexistente). */
export function activeScopeIdFor(userId: string): string | null {
  return activeMap()[userId] ?? null;
}

/** Fija (y persiste) la org activa del usuario y señala un cambio explícito. */
export function setActiveScopeIdFor(userId: string, scopeId: string): void {
  const current = activeMap()[userId] ?? null;
  const next = { ...activeMap(), [userId]: scopeId };
  setActiveMap(next);
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Persistencia best-effort; la señal en memoria ya cambió.
  }
  // Solo cuenta como "switch" si el destino cambia de verdad — así un re-set al
  // mismo scope (p. ej. re-activar el ya activo) no dispara el guard.
  if (current !== scopeId) {
    setSwitchEpoch((n) => n + 1);
  }
}

/** Accessor reactivo del epoch de cambio explícito de org (para el guard). */
export function orgSwitchEpoch(): number {
  return switchEpoch();
}

/** Solo para tests: resetea el estado en memoria releyendo localStorage. */
export function resetActiveOrgStateForTests(): void {
  setActiveMap(readPersisted());
}
